import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { AuthService } from '../../core/auth/services/auth.service';
import {
  IFullProject,
  IProjects,
  IRepositories,
  ISessionToken,
} from '../../models';
import config from '../../../config';

@Injectable({
  providedIn: 'root',
})
export class GithubService {
  apiUrl = config.API_GATEWAY_ENDPOINT;
  prid = '';

  constructor(
    readonly http: HttpClient,
    readonly _authService: AuthService,
  ) {
    this._authService.amplifyGetUserSession().forEach((session: any) => {
      this.prid = this._extractPridFromSession(session);
    });
  }

  async fetchRepositories(): Promise<IRepositories> {
    try {
      const response = await lastValueFrom(
        this.http.get<IRepositories>(`${this.apiUrl}/github/${this.prid}`),
      );
      return response;
    } catch (error) {
      console.error('Error fetching repositories:', error);
      throw error;
    }
  }

  async _fetchContentRepositories(
    ec2Repositories: IRepositories,
  ): Promise<IProjects> {
    try {
      const repositories = ec2Repositories.ec2Repositories;
      const keys = Object.keys(repositories);

      // Only split into chunks if there are more than 8 repositories
      if (keys.length > 8) {
        const ec2RepositoriesChunks = this._splitIntoChunks(ec2Repositories, 2);
        const responses = await Promise.all(
          ec2RepositoriesChunks.map((chunk) =>
            this.fetchContentRepositories(chunk),
          ),
        );
        return this._mergeResponsesFromContentRepositories(responses);
      } else {
        // If there are 8 or less, make a single request
        return await this.fetchContentRepositories(ec2Repositories);
      }
    } catch (error) {
      console.error('Error fetching content repositories:', error);
      throw error;
    }
  }

  async fetchContentRepositories(chunk: IRepositories): Promise<IProjects> {
    return lastValueFrom(
      this.http.post<IProjects>(`${this.apiUrl}/github`, chunk),
    );
  }

  _mergeResponsesFromContentRepositories(responses: IProjects[]): IProjects {
    const merged: IProjects = {};

    responses.forEach((response) => {
      Object.assign(merged, response);
    });

    return merged;
  }

  async _fetchContentStackEOL(projects: IProjects): Promise<IFullProject[]> {
    try {
      const keys = Object.keys(projects);

      // Only split into chunks if there are more than 8 repositories
      if (keys.length > 8) {
        const chunks = this._splitIntoChunks({ ec2Repositories: projects }, 2);
        const responses = await Promise.all(
          chunks.map((chunk) =>
            this.fetchContentStackEOL(chunk.ec2Repositories),
          ),
        );
        return this._mergeArrayResponses(responses);
      } else {
        // If there are 8 or less, make a single request
        return await this.fetchContentStackEOL(projects);
      }
    } catch (error) {
      console.error('Error fetching stack EOL:', error);
      throw error;
    }
  }

  async fetchContentStackEOL(projects: IProjects): Promise<IFullProject[]> {
    return lastValueFrom(
      this.http.post<IFullProject[]>(`${this.apiUrl}/eol`, {
        ec2Repositories: projects,
      }),
    );
  }

  _mergeArrayResponses(responses: IFullProject[][]): IFullProject[] {
    return responses.flat();
  }

  async fetchProjects(): Promise<IRepositories> {
    try {
      const response = await lastValueFrom(
        this.http.get<IRepositories>(`${this.apiUrl}/projects`),
      );
      return response;
    } catch (error) {
      console.error('Error fetching repositories:', error);
      throw error;
    }
  }

  _splitIntoChunks(data: any, numberOfChunks: number): any[] {
    const repositories = data.ec2Repositories;
    const keys = Object.keys(repositories);
    const chunkSize = Math.ceil(keys.length / numberOfChunks);
    const chunks: any[] = [];

    for (let i = 0; i < numberOfChunks; i++) {
      const start = i * chunkSize;
      const end = start + chunkSize;
      const chunkKeys = keys.slice(start, end);

      const chunk: any = { ec2Repositories: {} };
      chunkKeys.forEach((key) => {
        chunk.ec2Repositories[key] = repositories[key];
      });

      chunks.push(chunk);
    }

    return chunks;
  }

  _extractPridFromSession(session: ISessionToken): string {
    if (session?.tokens?.accessToken?.payload?.username) {
      const pridToken = session.tokens.accessToken.payload.username;
      const email = pridToken.split('@')[0];
      return email.split('_')[1] || email;
    }
    return '';
  }
}
